// ── Material metadata constants (v2 material category system) ──
import { ItemType } from '../types/loot.js';
/** Canonical material definitions keyed by ItemType value.
 *  Rates sourced from docs/design/material-economy-model.md §1. */
export const MATERIAL_DEFS = {
    [ItemType.MaterialWood]: {
        itemType: ItemType.MaterialWood,
        category: 'primary',
        displayName: 'Wood',
        baseGatherRate: 200,
        stackMax: 99,
    },
    [ItemType.MaterialStone]: {
        itemType: ItemType.MaterialStone,
        category: 'primary',
        displayName: 'Stone',
        baseGatherRate: 60,
        stackMax: 99,
    },
    [ItemType.MaterialThatch]: {
        itemType: ItemType.MaterialThatch,
        category: 'primary',
        displayName: 'Thatch',
        baseGatherRate: 80,
        stackMax: 99,
    },
    [ItemType.MaterialCloth]: {
        itemType: ItemType.MaterialCloth,
        category: 'secondary',
        displayName: 'Cloth',
        baseGatherRate: 20,
        stackMax: 99,
    },
    [ItemType.MaterialInk]: {
        itemType: ItemType.MaterialInk,
        category: 'secondary',
        displayName: 'Ink',
        baseGatherRate: 15,
        stackMax: 99,
    },
    [ItemType.MaterialWax]: {
        itemType: ItemType.MaterialWax,
        category: 'secondary',
        displayName: 'Wax',
        baseGatherRate: 15,
        stackMax: 99,
    },
    [ItemType.MaterialAsh]: {
        itemType: ItemType.MaterialAsh,
        category: 'secondary',
        displayName: 'Ash',
        baseGatherRate: 8,
        stackMax: 99,
    },
    [ItemType.MaterialThawedWater]: {
        itemType: ItemType.MaterialThawedWater,
        category: 'regional',
        displayName: 'Thawed Water',
        realmAffinity: 'frostmere',
        baseGatherRate: 10,
        stackMax: 99,
    },
    [ItemType.MaterialEmber]: {
        itemType: ItemType.MaterialEmber,
        category: 'regional',
        displayName: 'Ember',
        realmAffinity: 'ember_mire',
        baseGatherRate: 10,
        stackMax: 99,
    },
    [ItemType.MaterialSpiritsong]: {
        itemType: ItemType.MaterialSpiritsong,
        category: 'rare',
        displayName: 'Spiritsong',
        baseGatherRate: 0.2,
        stackMax: 99,
    },
};
/** All material ItemType values for quick membership checks */
export const MATERIAL_ITEM_TYPES = new Set(Object.values(MATERIAL_DEFS).map(d => d.itemType));
/**
 * Per-interaction yield record. All materials yield 1 unit per action;
 * the rate is gated by gather duration + cooldown in GATHER_NODE_CONFIGS.
 *
 * Source of truth: docs/design/material-economy-model.md §1, §8.
 */
export const GATHERING_YIELD = {
    [ItemType.MaterialWood]: { itemType: ItemType.MaterialWood, unitsPerHour: 200, unitsPerAction: 1 },
    [ItemType.MaterialStone]: { itemType: ItemType.MaterialStone, unitsPerHour: 60, unitsPerAction: 1 },
    [ItemType.MaterialThatch]: { itemType: ItemType.MaterialThatch, unitsPerHour: 80, unitsPerAction: 1 },
    [ItemType.MaterialCloth]: { itemType: ItemType.MaterialCloth, unitsPerHour: 20, unitsPerAction: 1 },
    [ItemType.MaterialInk]: { itemType: ItemType.MaterialInk, unitsPerHour: 15, unitsPerAction: 1 },
    [ItemType.MaterialWax]: { itemType: ItemType.MaterialWax, unitsPerHour: 15, unitsPerAction: 1 },
    [ItemType.MaterialAsh]: { itemType: ItemType.MaterialAsh, unitsPerHour: 8, unitsPerAction: 1 },
    [ItemType.MaterialThawedWater]: { itemType: ItemType.MaterialThawedWater, unitsPerHour: 10, unitsPerAction: 1 },
    [ItemType.MaterialEmber]: { itemType: ItemType.MaterialEmber, unitsPerHour: 10, unitsPerAction: 1 },
    [ItemType.MaterialSpiritsong]: { itemType: ItemType.MaterialSpiritsong, unitsPerHour: 0.2, unitsPerAction: 1 },
};
// ── Realm restoration gathering bonus (loot-and-materials.md §2.1) ────
/**
 * Restoration threshold (0–100%) at or above which the gathering bonus
 * activates. Per loot-and-materials.md §2.1: "+25% gathering rate in
 * realms at ≥50% restoration."
 */
export const RESTORATION_GATHER_THRESHOLD_PCT = 50;
/**
 * Gathering yield multiplier when restoration is at or above the
 * threshold. Applied multiplicatively to the base yield quantity.
 */
export const RESTORATION_GATHER_BONUS = 1.25;
/**
 * Calculate the gathering yield multiplier for a realm's current
 * restoration percentage. Returns 1.0 below threshold, 1.25 at or above.
 *
 * @param restorationPct - Realm restoration meter (0–100).
 * @returns Yield multiplier (1.0 or 1.25).
 */
export function getRestorationGatherMultiplier(restorationPct) {
    return restorationPct >= RESTORATION_GATHER_THRESHOLD_PCT
        ? RESTORATION_GATHER_BONUS
        : 1.0;
}
/**
 * Passive trickle materials and rates. Only these materials are produced
 * during Parked-Hearth state. Building materials (wood, stone, thatch)
 * are intentionally excluded — home progression requires active play.
 *
 * Source of truth: material-economy-model.md §1.5
 */
export const PASSIVE_TRICKLE_RATES = [
    { itemType: ItemType.MaterialWax, unitsPerHour: 4 },
    { itemType: ItemType.MaterialInk, unitsPerHour: 4 },
    { itemType: ItemType.MaterialCloth, unitsPerHour: 4 },
    // Ambient Food: 8/hour — produces Bread as the baseline food item.
    { itemType: ItemType.FoodBread, unitsPerHour: 8 },
];
/**
 * Maximum passive trickle bundles per day (per material-economy-model.md §1.5).
 * Beyond this cap, no more materials are produced until the next real day.
 */
export const PASSIVE_TRICKLE_MAX_BUNDLES_PER_DAY = 16;
//# sourceMappingURL=materials.js.map